import { useCallback } from 'react';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';
import { sanitizeRegionValue } from '../../../utils/escalateQueryUtils';
import { ALL_REGION_VALUE } from '../../../constants/escalateQueryDialog';

/**
 * Custom hook for fetching escalation query data
 * @param {Object} query - Query object
 * @param {string} region - Current region
 * @param {Object} stateSetters - Object with state setters from useEscalateQueryState
 * @returns {Object} Data fetching functions
 */
export const useEscalateQueryData = (query, region, stateSetters) => {
  const { showSnackbar } = useSnackbar();
  const {
    setCurrentUser,
    setTargets,
    setAllowedRoles,
    setLoadingOptions,
    setLoadError,
    setRegion: setRegionState,
  } = stateSetters;

  const fetchCurrentUser = useCallback(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        setCurrentUser(parsed);
        return;
      }
    } catch (error) {
      // Silently handle parse errors - user will be null
    }
    setCurrentUser(null);
  }, [setCurrentUser]);

  const fetchEscalationOptions = useCallback(
    async (regionValue) => {
      const queryId = query?._id || query?.id;
      if (!queryId) {
        setLoadError('Unable to load escalation options for this query.');
        setTargets([]);
        setAllowedRoles([]);
        return;
      }
      const sanitizedRegion = sanitizeRegionValue(regionValue || region);
      setLoadingOptions(true);
      setLoadError('');
      try {
        const params = {};
        if (sanitizedRegion && sanitizedRegion !== ALL_REGION_VALUE) {
          params.region = sanitizedRegion;
        }
        const response = await api.get(`/queries/${queryId}/escalation-options`, {
          params,
        });
        const data = response.data?.data || {};
        setAllowedRoles(data.allowedRoles || []);
        setTargets(Array.isArray(data.targets) ? data.targets : []);
        const preferredRegion = sanitizeRegionValue(
          data.defaultRegion || sanitizedRegion || region
        );
        setRegionState(preferredRegion);
        setLoadError('');
      } catch (error) {
        const status = error?.response?.status;
        const message =
          status === 404
            ? 'This query is no longer available for escalation.'
            : 'Failed to load escalation options';
        setLoadError(message);
        showSnackbar(message, 'error');
        setTargets([]);
        setAllowedRoles([]);
      } finally {
        setLoadingOptions(false);
      }
    },
    [query, region, showSnackbar, setTargets, setAllowedRoles, setLoadingOptions, setLoadError, setRegionState]
  );

  return {
    fetchCurrentUser,
    fetchEscalationOptions,
  };
};
